// 将浏览器录制音频编码为 PCMA 格式
// 复制自 https://github.com/rochars/alawmulaw/blob/master/lib/alaw.js

/* eslint-disable no-bitwise */

const LOG_TABLE = [
  1, 1, 2, 2, 3, 3, 3, 3, 4, 4, 4, 4, 4, 4, 4, 4, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5,
  5, 5, 5, 5, 5, 5, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6,
  6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7,
  7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7,
  7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7,
];

/**
 * Encode a 16-bit linear PCM sample as 8-bit A-Law.
 */
function aLawEncode(pcmSample) {
  let compandedValue;
  pcmSample = pcmSample === -32768 ? -32767 : pcmSample;
  const sign = (~pcmSample >> 8) & 0x80;
  if (!sign) {
    pcmSample *= -1;
  }
  if (pcmSample > 32635) {
    pcmSample = 32635;
  }
  if (pcmSample >= 256) {
    const exponent = LOG_TABLE[(pcmSample >> 8) & 0x7f];
    const mantissa = (pcmSample >> (exponent + 3)) & 0x0f;
    compandedValue = (exponent << 4) | mantissa;
  } else {
    compandedValue = pcmSample >> 4;
  }
  return compandedValue ^ (sign ^ 0x55);
}

/**
 * [-1, 1] 的 Float32 转成 Int16 的 PCM 编码
 */
function float32ToInt16PCM(sample) {
  let int16 = Math.round(sample * 32768);
  if (int16 > 32767) int16 = 32767;
  if (int16 < -32768) int16 = -32768;
  return int16;
}

export function encodeAudioData(float32Array) {
  // console.log(111, float32Array);
  const uint8Array = new Uint8Array(float32Array.length);
  float32Array.forEach((sample, i) => {
    // [-1, 1] 的 Float32 转成 Int16 的 PCM 编码
    const int16 = float32ToInt16PCM(sample);
    // 再转成 Uint8 的 PCMA 编码
    uint8Array[i] = aLawEncode(int16);
  });
  return uint8Array;
}

export function reSampleAndEncodeAudioData(dataList, bufferSize, ratio) {
  const size = Math.floor((dataList.length * bufferSize) / ratio);
  const uint8Array = new Uint8Array(size);
  for (let i = 0; i < size; i++) {
    // 按 ratio 重新抽样
    const j = Math.floor((i * ratio) / bufferSize);
    const k = (i * ratio) % bufferSize;
    const sample = dataList[j][k];
    // [-1, 1] 的 Float32 转成 Int16 的 PCM 编码
    const int16 = float32ToInt16PCM(sample);
    // 再转成 Uint8 的 PCMA 编码
    uint8Array[i] = aLawEncode(int16);
  }
  return Buffer.from(uint8Array).toString("base64");
}

/**
 * 数据合并压缩
 * 根据输入和输出的采样率压缩数据，
 * 比如输入的采样率是48k的，我们需要的是（输出）的是8k的，由于48k与8k是6倍关系，
 * 所以输入数据中每隔6取1位
 *
 * @param {float32array} data       [-1, 1]的pcm数据
 * @param {number} inputSampleRate  输入采样率
 * @param {number} outputSampleRate 输出采样率
 * @returns  {float32array}         压缩处理后的二进制数据
 */
export function compress(data, inputSampleRate, outputSampleRate) {
   console.log(data);
  // 压缩，根据采样率进行压缩
  let rate = inputSampleRate / outputSampleRate,
    compression = Math.max(rate, 1),
    lData = data.left,
    rData = data.right,
    length = Math.floor((lData.length + rData.length) / rate),
    result = new Float32Array(length),
    index = 0,
    j = 0;
  // 循环间隔 compression 位取一位数据
  while (index < length) {
    // 取整是因为存在比例compression不是整数的情况
    let temp = Math.floor(j);

    result[index] = lData[temp];
    index++;

    if (rData && rData.length) {
      /*
       * 双声道处理
       * e.inputBuffer.getChannelData(0)得到了左声道4096个样本数据，1是右声道的数据，
       * 此处需要组和成LRLRLR这种格式，才能正常播放，所以要处理下
       */
      result[index] = rData[temp];
      index++;
    }

    j += compression;
  }
  // 返回压缩后的一维数据
  return result;
}

/**
 * 转换到我们需要的对应格式的编码
 *
 * @param {float32array} bytes      pcm二进制数据
 * @param {number}  sampleBits      采样位数
 * @param {boolean} littleEdian     是否是小端字节序
 * @returns {dataview}              pcm二进制数据
 */
export function encodePCM(bytes, sampleBits, littleEdian = true) {
  console.log("PCM", bytes);
  let offset = 0,
    dataLength = bytes.length * (sampleBits / 8),
    buffer = new ArrayBuffer(dataLength),
    data = new DataView(buffer);

  // 写入采样数据
  if (sampleBits === 8) {
    for (let i = 0; i < bytes.length; i++, offset++) {
      // 范围[-1, 1]
      let s = Math.max(-1, Math.min(1, bytes[i]));
      // 8位采样位划分成2^8=256份，它的范围是0-255;
      // 对于8位的话，负数*128，正数*127，然后整体向上平移128(+128)，即可得到[0,255]范围的数据。
      let val = s < 0 ? s * 128 : s * 127;
      val = +val + 128;
      data.setInt8(offset, val);
    }
  } else {
    for (let i = 0; i < bytes.length; i++, offset += 2) {
      let s = Math.max(-1, Math.min(1, bytes[i]));
      // 16位的划分的是2^16=65536份，范围是-32768到32767
      // 因为我们收集的数据范围在[-1,1]，那么你想转换成16位的话，只需要对负数*32768,对正数*32767,即可得到范围在[-32768,32767]的数据。
      data.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, littleEdian);
    }
  }

  return data;
}
export function encodePCMA(bytes) {
  console.log("PCMA", bytes);
  let dataLength = bytes.length * 1,
    buffer = new ArrayBuffer(dataLength);
  const view = new DataView(buffer);
  const numSamples = buffer.byteLength;

  for (let i = 0; i < numSamples; i++) {
    const value = Math.max(
      -1,
      Math.min(1, buffer[i])
    ); /* 从 PCMA 数据中读取值 */
    const amplitude = (value & 0xf0) >> 4;
    const sign = value & 0x0f;
    const pcmValue = decodeALaw(amplitude, sign); // 解码 PCMA 值为 PCM 值
    view.setInt8(i, pcmValue); // 将 PCM 值写入 DataView
  }
  return view;
}
function decodeALaw(amplitude, sign) {
  const exponent = (amplitude >> 4) & 0x07;
  const mantissa = amplitude & 0x0f;

  let sample = (mantissa << 4) | 0x08;
  sample = sample << (exponent + 3);

  if (sign === 0) {
    sample = -sample;
  }

  return sample;
}


